'use client';

import { useState, type ReactNode } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { ArrowDown01Icon, ArrowRight01Icon } from '@terra/ui-icons';
import { type BaseArtifactDto } from '@terra/shared-contracts-artifact';
import { useTranslations } from 'next-intl';
import { Text } from '../../components/styled/text';
import { ReadOnlyCodeEditor } from '../chat/components/ReadOnlyCodeEditor';
import { useArtifactsOptional } from '../chat/trace-view/ArtifactsProvider';
import { RecordingDialogButton } from '../chat/trace-view/components/RecordingDialogButton';
import { MarkdownPreview } from '../markdown-preview';
import { EvidenceCardMediaSection } from './EvidenceCardMediaSection';

type EvidenceCardWithArtifactProps = {
  artifact: BaseArtifactDto;
  explanation: string;
  index: number;
  defaultExpanded?: boolean;
  actions?: ReactNode;
  media?: ReactNode;
  escapeExplanationRawHtml?: boolean;
};
type CodeLanguage = 'python' | 'javascript' | 'json' | 'text';
type ArtifactDisplayConfig = {
  actionLabel: string;
  resultLabel: string;
  actionLanguage: CodeLanguage;
  resultLanguage: CodeLanguage;
};

function resolveDisplayConfig(type: string): ArtifactDisplayConfig {
  switch (type) {
    case 'PYTHON':
      return {
        actionLabel: 'Code',
        resultLabel: 'Result',
        actionLanguage: 'python',
        resultLanguage: 'text',
      };
    case 'HTTP':
      return {
        actionLabel: 'Request',
        resultLabel: 'Response',
        actionLanguage: 'text',
        resultLanguage: 'text',
      };
    case 'CLI':
      return {
        actionLabel: 'Command',
        resultLabel: 'Output',
        actionLanguage: 'text',
        resultLanguage: 'text',
      };
    case 'BROWSER':
      return {
        actionLabel: 'Browser Action',
        resultLabel: 'Browser Result',
        actionLanguage: 'text',
        resultLanguage: 'text',
      };
    case 'TEXT':
      return {
        actionLabel: 'Content',
        resultLabel: 'Result',
        actionLanguage: 'text',
        resultLanguage: 'text',
      };
    default:
      return {
        actionLabel: 'Action',
        resultLabel: 'Result',
        actionLanguage: 'text',
        resultLanguage: 'text',
      };
  }
}

function formatArtifactType(type: string): string {
  return type
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function EvidenceCardWithArtifact({
  artifact,
  explanation,
  index,
  defaultExpanded = false,
  actions,
  media,
  escapeExplanationRawHtml = false,
}: EvidenceCardWithArtifactProps) {
  const t = useTranslations('designSystem.artifacts');
  const artifacts = useArtifactsOptional();
  const [expandedSections, setExpandedSections] = useState({
    action: defaultExpanded,
    result: false,
  });
  const displayConfig = resolveDisplayConfig(artifact.type);
  const toggleSection = (section: 'action' | 'result') => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const recordingToolCallId =
    artifact.toolCallId &&
    artifacts?.getRecordingByToolCallId(artifact.toolCallId)
      ? artifact.toolCallId
      : null;

  const hasAction = Boolean(artifact.actionContent);
  const hasResult = Boolean(artifact.resultContent);
  const artifactTypeLabel = formatArtifactType(artifact.type);

  return (
    <Box
      borderWidth="1px"
      borderColor="border.neutral.muted"
      borderRadius="md"
      bg="bg.subtle"
      overflow="hidden"
      width="100%"
    >
      <HStack
        justify="space-between"
        alignItems="center"
        px={3}
        h="36px"
        bg="bg.subtle"
        borderBottomWidth="1px"
        borderBottomColor="border.neutral.muted"
      >
        <HStack gap={1.5} alignItems="center" minW={0}>
          <Text fontSize="xs" fontWeight="medium" color="fg.default" truncate>
            {t('evidenceLabel', { index: index + 1 })}
          </Text>
          <Text fontSize="xs" color="fg.muted" truncate>
            {artifactTypeLabel}
          </Text>
        </HStack>
        {recordingToolCallId || actions ? (
          <HStack gap={1} alignItems="center">
            {recordingToolCallId ? (
              <RecordingDialogButton toolCallId={recordingToolCallId} />
            ) : null}
            {actions}
          </HStack>
        ) : null}
      </HStack>

      {explanation && (
        <Box
          bg="bg.subtle"
          px={3}
          py={2}
          color="fg.default"
          borderBottomWidth="1px"
          borderBottomColor="border.neutral.muted"
        >
          <MarkdownPreview
            source={explanation}
            fontSize={12}
            compact
            escapeRawHtml={escapeExplanationRawHtml}
          />
        </Box>
      )}

      <VStack gap={0} alignItems="stretch">
        {hasAction && (
          <Box
            bg="bg.subtle"
            borderBottomWidth={hasResult ? '1px' : '0'}
            borderBottomColor="border.neutral.muted"
            width="100%"
          >
            <HStack
              px={3}
              h="32px"
              cursor="pointer"
              onClick={() => toggleSection('action')}
              width="100%"
              gap={1.5}
              alignItems="center"
            >
              <Box display="flex" alignItems="center" color="fg.muted">
                {expandedSections.action ? (
                  <ArrowDown01Icon size={10} />
                ) : (
                  <ArrowRight01Icon size={10} />
                )}
              </Box>
              <Text fontSize="xs" fontWeight="medium">
                {displayConfig.actionLabel}
              </Text>
            </HStack>

            {expandedSections.action && (
              <Box
                borderTopWidth="1px"
                borderColor="border.neutral.muted"
                bg="bg.subtle"
                maxH="300px"
                overflow="hidden"
              >
                <ReadOnlyCodeEditor
                  value={artifact.actionContent ?? ''}
                  maxHeight="300px"
                  minHeight="44px"
                  showLineNumbers
                  language={displayConfig.actionLanguage}
                />
              </Box>
            )}
          </Box>
        )}

        {hasResult && (
          <Box bg="bg.subtle" width="100%">
            <HStack
              px={3}
              h="32px"
              cursor="pointer"
              onClick={() => toggleSection('result')}
              width="100%"
              gap={1.5}
              alignItems="center"
            >
              <Box display="flex" alignItems="center" color="fg.muted">
                {expandedSections.result ? (
                  <ArrowDown01Icon size={10} />
                ) : (
                  <ArrowRight01Icon size={10} />
                )}
              </Box>
              <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                {displayConfig.resultLabel}
              </Text>
            </HStack>

            {expandedSections.result && (
              <Box
                borderTopWidth="1px"
                borderColor="border.neutral.muted"
                bg="bg.subtle"
                maxH="300px"
                overflow="hidden"
              >
                <ReadOnlyCodeEditor
                  value={artifact.resultContent ?? ''}
                  maxHeight="300px"
                  minHeight="44px"
                  language={displayConfig.resultLanguage}
                />
              </Box>
            )}
          </Box>
        )}

        {!hasAction && !hasResult && !media && (
          <Box px={3} py={2}>
            <Text fontSize="xs" color="fg.muted">
              {t('noArtifactContentAvailable')}
            </Text>
          </Box>
        )}
      </VStack>

      <EvidenceCardMediaSection media={media} />
    </Box>
  );
}
